import Vue from 'vue';
import Vuex from 'vuex';
import * as common from '@/utils/common';
import { jwtHelperMyStage } from '@/utils/jwt';
import logger from '@/modules/Logger';

import uaParser from './modules/uaParser';
import material from './modules/material';
import connection from './modules/connection';
import lesson from './modules/lesson';
import layout from './modules/layout';
import socketio from './modules/socketio';
import devices from './modules/devices';
import triallesson from './modules/triallesson';
import video from './modules/video';
import recorder from './modules/recorder';
import videoRtc from './modules/videoRtc';
import vocabulary from './modules/vocabulary';
import vocabularymanagement from './modules/vocabularymanagement';
import trainerTag from './modules/trainerTag';
import htmlMaterial from './modules/htmlMaterial';
import modal from './modules/modal';
import booking from './modules/booking';
import favorites from './modules/favorites';
import lessonHabit from './modules/lessonHabit';
import customerFeedback from './modules/customerFeedback';
import chatHistory from './modules/chatHistory';
import userOtherSetting from './modules/userOtherSetting';
import { bizmatesApi } from '@/utils/bizmatesApi';
import {
  programs,
  lessonStatus,
  providers,
  errorCodes,
  serviceIds,
  enableCanaryCheck,
  contractTypes,
  mystageServiceIdHeaderName
} from '@/utils/constants';
import { store } from '@/utils/common';
import { apolloProvider, apolloClient } from '@/modules/myStageGraphQL';
import { GET_BETA_ACCESS } from '@/graphql/betaAccess';
import { GET_MYSTAGE_LESSON_TOKEN } from '@/graphql/mystageLessonToken';
import { SALES_TRIAL_LESSON_TOKEN } from '@/graphql/salesTrial';
import { getApiMethod } from '@/modules/apiSelector';
import { isZipanHost } from '@/utils/urlHelper';

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    chatHistory,
    uaParser,
    material,
    connection,
    lesson,
    layout,
    socketio,
    devices,
    triallesson,
    video,
    recorder,
    videoRtc,
    vocabulary,
    vocabularymanagement,
    trainerTag,
    htmlMaterial,
    modal,
    booking,
    favorites,
    lessonHabit,
    customerFeedback,
    userOtherSetting
  },
  state: {
    issuedAt: null,
    lessonToken: null,
    socketToken: null,
    lessonId: null,
    userId: null,
    localNickname: null,
    remoteNickname: null,
    accType: null,
    provider: null,
    credential: null,
    webRtcUid: null,
    programId: null,
    studentRank: null,
    lessonBookRank: null,
    studentLessonNo: null,
    lessonStatus: null,
    lessonStart: null,
    lessonEnd: null,
    gracePeriodEnd: null,
    videoProfile: null,
    screenShareProfile: null,
    jwtError: null,
    unverifiedPayload: null,
    hasNextLesson: null,
    notifyPeriod: null,
    isTest: false,
    isTrial: false,
    isDevTest: false,
    jwtKey: null,
    jwtValue: null,
    isFirstLesson: null,
    contractType: null,
    isUserAuth: true,
    chatboxEnd: null,
    instantModal: true,
    bizmates_token: null,
    teacherId: null,
    fuelToken: null,
    remoteUserId: null,
    localExternalId: null,
    remoteExternalId: null,
    hasTranscription: false,
    hasRecording: false,
    displayPopup: false,
    hidePopup: true,
    isRevamp: null,
    unverifiedLessonId: null,
    legacy: null,
    enableBeta: 0,
    // Use this if you need the actual account type of the user
    // the other state.acctType is from the lesson token, 
    // which converts admin account type to student.
    actualAcctType: null,
    devTestFeatures: null,
    enabledCloudRecording: 0,
    isReassessment: false
  },
  getters: {
    isStudent: state => {
      return state.accType === 's' || state.isTrial;
    },
    isStudentGlobalState: (state, getters) => {
      return getters.isStudent || [state.chatHistory.accountType, state.htmlMaterial.userType, state.actualAcctType].includes('s');
    },
    isTrainer: state => {
      return state.accType === 't' && !state.isTrial;
    },
    isBizmatesLesson: state => {
      return state.programId === programs.bizmates;
    },
    isAssessmentLesson: state => {
      return state.programId === programs.assessment;
    },
    isAssistLesson: state => {
      return state.programId === programs.assist_lesson;
    },
    isDiscoveryLesson: state => {
      return state.programId === programs.discovery;
    },
    isReskillingLesson: state => {
      return state.programId === programs.reskilling;
    },
    isOtherLesson: state => {
      return state.programId === programs.other;
    },
    isCorpLesson: state => {
      return state.programId === programs.corporate;
    },
    isFirstAssessment: (state, getters) => {
      return getters.isAssessmentLesson && !state.isReassessment;
    },
    isWaitingForLesson: state => {
      return state.lessonStatus === lessonStatus.scheduled;
    },
    isLessonOngoing: state => {
      return state.lessonStatus === lessonStatus.ongoing;
    },
    isLessonEnded: state => {
      return (
        state.lessonStatus === lessonStatus.finished ||
        state.lesson.isLessonBeyondGracePeriod ||
        state.socketio.voluntaryLessonTermination
      );
    },
    isWarmUpPeriod: (state) => {
      return state.lessonStatus === lessonStatus.warmup;
    },
    isWaitingOrWarmUpPeriod: (state, getters) => {
      return getters.isWaitingForLesson || getters.isWarmUpPeriod;
    },
    isLessonTest: state => {
      return state.isTest || state.isDevTest;
    },
    jwtKeyValue: state => {
      return {
        k: state.jwtKey,
        v: state.jwtValue
      };
    },
    logTransportToken: (state, getters) => {
      return state.isTrial
        ? { jwtKeyValue: getters.jwtKeyValue }
        : { lessonToken: state.lessonToken };
    },
    isToEnableVideoLesson: (state, getters) => {
      return (
        state.accType === 's' &&
        getters.hasMaterialSupport &&
        !state.isReassessment &&
        !state.isTest &&
        !state.isTrial &&
        !getters.isLessonOngoing
      );
    },
    isAmazonChime: state => {
      return state.provider === providers.awschime;
    },
    nonActualLesson(state) {
      return state.isTest || state.isTrial || state.isDevTest;
    },
    getIat(state) {
      return state.issuedAt;
    },
    isLegacy(state) {
      return state.legacy;
    },
    isZipanService(state) {
      return [state.serviceId, state.chatHistory.serviceId].includes(serviceIds.zipan);
    },
    displayLanguageSettings(state, getters) {
      return getters.isZipanService && getters.isStudent;
    },
    hasMaterialSupport: (state, getters) => {
      return (
        getters.isAssessmentLesson ||
        getters.isBizmatesLesson ||
        getters.isOtherLesson ||
        getters.isReskillingLesson ||
        getters.isCorpLesson
      );
    },
    isChimeB2BUser: (state, getters) => {
      const isB2B = contractTypes
        .filter(contract => contract.label === 'B2B')
        .some(contract => contract.id === parseInt(state.contractType, 10));
      return getters.isAmazonChime && isB2B;
    }
  },
  mutations: {
    getLessonInfo(state, lessonToken) {
      state.lessonToken = lessonToken;

      const lessonInfo = jwtHelperMyStage.verify(lessonToken);

      if (!lessonInfo) {
        state.jwtError = jwtHelperMyStage.jwtError;
        state.unverifiedPayload = jwtHelperMyStage.unverifiedPayload;

        logger.error('[JwtInvalidToken] Invalid token',{
          event_type: 52,
          lesson_id: this.lessonId
        });
        logger.info('Force sending of logs', { operation: 'send-batch-log' });

        if (jwtHelperMyStage.unverifiedPayload.a && jwtHelperMyStage.unverifiedPayload.a === 's') {
          // For students
          jwtHelperMyStage.logToken(null, 'Redirecting to homepage');
          common.redirectToHome(errorCodes.ME001);
        } else {
          // For trainers
          jwtHelperMyStage.logToken(null, 'Redirecting to jwt-error page');
          common.redirectToJwtError();
        }
      }

      state.issuedAt = lessonInfo.iat;
      state.lessonId = lessonInfo.l;
      state.userId = lessonInfo.sub;
      state.remoteUserId = lessonInfo.sub === lessonInfo.s ? lessonInfo.t : lessonInfo.s;
      state.accType = lessonInfo.a;
      state.localNickname = lessonInfo.a === 's' ? lessonInfo.n : lessonInfo.m;
      state.remoteNickname = lessonInfo.a === 's' ? lessonInfo.m : lessonInfo.n;
      state.provider = lessonInfo.w;
      state.credential = lessonInfo.c;
      state.programId = lessonInfo.p;
      state.studentRank = lessonInfo.o;
      state.lessonBookRank = lessonInfo.r;
      state.studentLessonNo = lessonInfo.ln;
      state.lessonStatus = lessonInfo.st;
      state.lessonStart = lessonInfo.b;
      state.lessonStartWarmUp = lessonInfo.b - lessonInfo.wup;
      state.lessonEnd = lessonInfo.e;
      state.gracePeriodEnd = lessonInfo.g;
      state.videoProfile = lessonInfo.vp;
      state.screenShareProfile = lessonInfo.sp;
      state.isTest = lessonInfo.isTest ? true : false;
      state.isDevTest = !!lessonInfo.is_dev_test;
      state.notifyPeriod = lessonInfo.nt;
      state.isTrial = !!lessonInfo.isTrial;
      state.jwtKey = lessonInfo.k;
      state.jwtValue = lessonInfo.v;
      socketio.state.hasNextLessonStatus = lessonInfo.hnl;
      state.isFirstLesson = lessonInfo.fl;
      state.contractType = lessonInfo.ct;
      state.isUserAuth = true;
      state.chatboxEnd = lessonInfo.cbe;
      state.bizmates_token = lessonInfo.bt;
      state.teacherId = lessonInfo.t;
      state.fuelToken = lessonInfo.ft;
      state.localExternalId = lessonInfo.local_external_id;
      state.remoteExternalId = lessonInfo.remote_external_id;
      state.isRevamp = lessonInfo.rv;
      state.serviceId = lessonInfo.service_id || null;
      state.enabledCloudRecording = lessonInfo.enabled_recording;
      state.isReassessment = lessonInfo.is_reassessment || false;
      state.webRtcUid = lessonInfo.agora_uid || lessonInfo.sub;

      this.commit('userOtherSetting/setRemoteStudentCountryId', lessonInfo.remote_student_country_id);

      common.checkUserLocalTime(lessonInfo.l);
    },
    activateTranscribe(state, payload) {
      state.hasTranscription = payload;
      store.setTranscribeState(state.lessonId, payload);
    },
    activateAgoraRecord(state, payload) {
      state.hasRecording = payload;
      store.setAgoraRecordState(state.lessonId, payload);
    },
    setIsUserAuth(state, payload) {
      state.isUserAuth = payload;
    },
    getStudentPopup(state, payload){
      state.displayPopup = payload['display_modal'];
      state.hidePopup = payload['hide_modal'];
    },
    setUnverifiedLessonId(state, payload) {
      state.unverifiedLessonId = payload;
    },
    setLegacy(state, payload) {
      if (payload == undefined) {
        return;
      }

      state.legacy = payload;
    },
    setEnableBeta(state, payload) {
      state.enableBeta = payload ?? false;
    },
    setAccType(state, payload) {
      if (payload) {
        state.accType = payload;
      }
    },
    setActualAccType(state, payload) {
      if (payload) {
        state.actualAcctType = payload;
      }
    },
    setDevTestFeatures(state, payload) {
      state.devTestFeatures = payload;
    },
    setCredential(state, payload) {
      state.credential = payload;
    },
    setWebRtcUid(state, payload) {
      state.webRtcUid = payload;
    },
    setSocketToken(state, payload) {
      state.socketToken = payload;
    }
  },
  actions: {
    async activateTranscribe({ commit }, params) {
      logger.info('[Recording][AWS Chime] Recording is starting to run...');
      const apiMethod = getApiMethod('executeRecording');
      await apiMethod(params, (data) => {
        commit('activateTranscribe', data.status)
      });   
    },
    async activateAgoraRecord({ state, commit }, params) {
      if (!state.enabledCloudRecording) {
        logger.debug('[Recording][Agora Cloud] Cloud Recording is not enabled for this lesson');
        return;
      }
      commit('activateAgoraRecord', true);
      logger.info('[Recording][Agora Cloud] Recording is starting to run...');
      const apiMethod = getApiMethod('executeRecording');
      await apiMethod(params, (data) => {
        commit('activateAgoraRecord', data.status);
      }); 
    },
    async getLessonInfo({ commit, dispatch }, params) {
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      commit('setUnverifiedLessonId', params.lessonId);
      
      let apiMethod = getApiMethod('getLessonToken');
      try {
        const { result, token, socketToken, errMessage, } = await apiMethod(params.lessonId);
        if (result) {
          commit('getLessonInfo', token);
          commit('setSocketToken', socketToken);

          dispatch('socketio/initializeSocket', { lesson_id: params.lessonId });
          return false; // No error message
        } else {
          return errMessage; // Forward the err message
        }
      } catch (error) {
        logger.error('Failed to retrieve lesson token', error);
        return error.message;
      }
    },
    async checkApiMigrationCanaryUser({ commit }, params) {
      let logPrefix = '[Canary Release] ';

      if (!enableCanaryCheck || isZipanHost) {
        logger.info(`${logPrefix}canary checking is disabled`, {
          operation: 'detect-canary-user',
          lesson_id: params.lessonId || ''
        }); 
        return;
      }

      const client = apolloProvider.defaultClient;
      // Canary user checking
      let enableBeta = 0; 
      let accType = null;
      let betaAccess = null;

      if (!params.legacy) {
        try {
          const { data } = await client.query({
            query: GET_BETA_ACCESS,
            variables: {
              student_book_id: params.lessonId && Number(params.lessonId),
              route_name: params.routeName ?? ''
            }
          });
          
          betaAccess = data?.getBetaAccess || {};
          enableBeta = betaAccess.inherited_beta_level ?? betaAccess.enable_beta;
          accType = betaAccess.role_code;
        } catch (error) {
          logger.error(`${logPrefix} Could not verify beta access, continue as non-canary user`, {
            operation: 'detect-canary-user',
            error: error.message,
            lesson_id: params.lessonId || ''
          }); 
        }
        
        let logMessage;

        if (betaAccess?.inherited_beta_level != null) {
          const prefix = params.routeName === 'TestTrainer' ? 'Test Lesson — ' : '';
          logMessage = `${prefix}Trainer inherited beta access level: ${betaAccess.inherited_beta_level}`;
        } else if (enableBeta > 0) {
          logMessage = `User is included with access level: ${enableBeta}`;
        } else {
          logMessage = 'User is not included';
        }
        logger.info(logPrefix + logMessage, {
          operation: 'detect-canary-user',
          lesson_id: params.lessonId || '',
          initial_beta_level: betaAccess?.enable_beta,
          inherited_beta_level: betaAccess?.inherited_beta_level
        }); 
      }
      
      // commit canary user result for reusability
      commit('setEnableBeta', enableBeta);
      commit('setActualAccType', accType);

      return enableBeta > 0;
    },
    async getMyStageLessonToken({ commit, getters }, params) {
      const client = apolloProvider.defaultClient;
      let responseStatus = false;
      
      try {
        const { data } = await client.query({
          query: GET_MYSTAGE_LESSON_TOKEN,
          fetchPolicy: 'no-cache',
          variables: {
            student_book_id: parseInt(params.lessonId)
          }
        });

        if (data && data.mystage_lesson && data.mystage_lesson.token) {
          commit('getLessonInfo', data.mystage_lesson.token);
          common.sendUserAgentLogData(getters.isStudent);
        } else {
          responseStatus = 'No token data found';
        }
      } catch (error) {
        console.error('GraphQL query failed:', error.message);
        logger.error('Mystage Lesson Token request failed: ' + error.message, { event_type: 36, lesson_id: params.lessonId || '' });
        responseStatus = error;
      }

      return responseStatus;
    },
    async getLegacyMyStageLessonToken({ commit, getters }, params) {
      let responseStatus = false;

      await bizmatesApi.get('lesson/token', {
        params: { id: params.lessonId, t: Date.now() },
        timeout: 10000
      }, response => {
        commit('getLessonInfo', response.data);
        common.sendUserAgentLogData(getters.isStudent);
      }, err => {
        logger.error('Token request failed: ' + err.message,{
          event_type: 36,
          lesson_id: params.lessonId || ''
        }); // handle err to continue the flow of rerequest token functionality
        responseStatus = err;
      });

      return responseStatus;
    },
    async getAdminTrainerLessonInfo({ commit }, params) {
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      let apiMethod = getApiMethod('getAdminTrainerLessonToken');
      await apiMethod(params.lessonId, (token) => {
        commit('getLessonInfo', token);
      });
    },
    async getDevTestLessonInfo({ state, commit }, params) {
      commit('setEnableBeta', 4); // set high enable beta
      commit('setActualAccType', 'a');
      const features = params ?? state.devTestFeatures;

      const getAdminDevTestToken = getApiMethod('getAdminDevTestToken');
      const token = await getAdminDevTestToken(features);
      commit('getLessonInfo', token);
      commit('setDevTestFeatures', features);
    },
    async getTestLessonInfo({ commit }, params) {
      // Will generate test token to be use as a backdoor entry on production environment
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      await bizmatesApi.get('lesson/testToken', {
        params: {
          is_student: params.isStudent,
          id: params.lessonId,
          partner: params.partnerId,
          webrtc: params.webrtcId
        },
        timeout: 3000
      }, response => {
        commit('getLessonInfo', response.data);
      });
    },
    async getTrialLessonInfo({ commit }, params) {
      // Request trial lesson token from MyStage Backend GraphQL endpoint;
      // when a successful response is received, set the lesson info accordingly.
      const { data } = await apolloClient.query({
        query: SALES_TRIAL_LESSON_TOKEN,
        variables: {
          lesson_id: parseInt(params.lessonId, 10),
          sales_potential_company_id: parseInt(params.pCompanyId, 10),
          hash_code: params.hashCode,
          user: parseInt(params.user, 10)
        },
        context: {
          authRequired: false,
          headers: {
            [mystageServiceIdHeaderName]: params.serviceId
          }
        },
        fetchPolicy: 'no-cache'
      });

      const token = data?.salesTrialLessonToken?.token;
      if (!token) {
        throw new Error('Trial lesson token not found');
      }

      commit('getLessonInfo', token);
    },
    async getStudentPopup({ commit }, params) {
      let studentPopup = [];
      await bizmatesApi.get('lesson/studentPopup', { 
        params: { userId: params.userId }
      },
      response => {
        studentPopup['student_id'] = response.data.student_id;
        studentPopup['display_modal'] = response.data.display_modal;
        studentPopup['hide_modal'] = response.data.hide_modal;
        commit('getStudentPopup', studentPopup);
      },
      error => {
        studentPopup['student_id'] = null;
        studentPopup['display_modal'] = null;
        studentPopup['hide_modal'] = null;
        console.error(error);
      });

      return studentPopup;
    },
    setLegacy({ commit }, params) {
      commit('setLegacy', params.legacy);
    }
  }
});
